import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import { getOrgRepos } from '../utils/methods';
import { GitFork, Check, X, Edit, Trash2, Plus, Star, Building2, Users, CheckCircle, Tag, Clock, Filter, RefreshCw } from 'lucide-react';

const TABS = {
  GITHUB_ORG: 'github-org',
  USER_SUBMISSIONS: 'user-submissions',
  APPROVED: 'approved'
};

const ManageRepos = () => {
  const [activeTab, setActiveTab] = useState(TABS.GITHUB_ORG);
  const [githubRepos, setGithubRepos] = useState([]);
  const [dbRepos, setDbRepos] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingGithub, setFetchingGithub] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRepo, setEditingRepo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    githubUrl: '',
    language: '',
    isPrivate: false,
    isEligible: true,
    isApproved: false,
    source: 'org',
    pointsMultiplier: 1,
    sponsorId: null
  });

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      try {
        const auth = JSON.parse(localStorage.getItem('auth'))
        const token = auth?.token
        if (!token) {
          window.location.href = '/admin/login'
          return
        }
        const headers = { Authorization: `Bearer ${token}` };

        const [reposRes, submissionsRes, sponsorsRes, metricsRes] = await Promise.all([
          axios.get('/admin/repos', { headers }),
          axios.get('/admin/submissions', { headers }),
          axios.get('/admin/sponsors', { headers }),
          axios.get('/admin/metrics', { headers })
        ]);

        if (cancelled) return

        console.log('Repos response:', reposRes.data);
        console.log('Submissions response:', submissionsRes.data);

        // Repos endpoint returns array directly
        setDbRepos(Array.isArray(reposRes.data) ? reposRes.data : []);
        // Submissions endpoint returns { submissions: [...] }
        setSubmissions(Array.isArray(submissionsRes.data?.submissions) ? submissionsRes.data.submissions : []);
        setSponsors(Array.isArray(sponsorsRes.data) ? sponsorsRes.data : []);
        setMetrics(Array.isArray(metricsRes.data) ? metricsRes.data : []);
        setLoading(false);
      } catch (error) {
        if (cancelled) return
        console.error('Error fetching data:', error);
        if (error.response?.status === 401) {
          window.location.href = '/admin/login'
        }
        setLoading(false);
      }
    }

    fetchData()

    return () => {
      cancelled = true
    }
  }, []);

  // Refetch function for manual refresh
  const refetchData = async () => {
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      const headers = { Authorization: `Bearer ${token}` };

      const [reposRes, submissionsRes, sponsorsRes, metricsRes] = await Promise.all([
        axios.get('/admin/repos', { headers }),
        axios.get('/admin/submissions', { headers }),
        axios.get('/admin/sponsors', { headers }),
        axios.get('/admin/metrics', { headers })
      ]);

      setDbRepos(Array.isArray(reposRes.data) ? reposRes.data : []);
      setSubmissions(Array.isArray(submissionsRes.data?.submissions) ? submissionsRes.data.submissions : []);
      setSponsors(Array.isArray(sponsorsRes.data) ? sponsorsRes.data : []);
      setMetrics(Array.isArray(metricsRes.data) ? metricsRes.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        window.location.href = '/admin/login'
      }
    }
  }

  const fetchGithubRepos = async () => {
    try {
      setFetchingGithub(true);
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }

      // Fetch GitHub org repos using backend proxy
      const orgName = process.env.REACT_APP_GITHUB_ORG || 'fossit-org';
      const response = await getOrgRepos(orgName, { per_page: 100, sort: 'updated' });

      setGithubRepos(response.data);
    } catch (error) {
      console.error('Error fetching GitHub repos:', error);
      alert('Failed to fetch GitHub repos. Make sure you have access to the organization.');
      setGithubRepos([]);
    } finally {
      setFetchingGithub(false);
    }
  };

  const handleCreateRepo = async (e) => {
    e.preventDefault();
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      await axios.post('/admin/repos', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateModal(false);
      resetForm();
      refetchData();
    } catch (error) {
      console.error('Error creating repo:', error);
      alert('Failed to create repository');
    }
  };

  const handleUpdateRepo = async (e) => {
    e.preventDefault();
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      await axios.put(`/admin/repos/${editingRepo.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingRepo(null);
      resetForm();
      refetchData();
    } catch (error) {
      console.error('Error updating repo:', error);
      alert('Failed to update repository');
    }
  };

  const handleApproveSubmission = async (submissionId, approved) => {
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      
      const response = await axios.put(`/admin/submissions/${submissionId}`, {
        status: approved ? 'approved' : 'rejected',
        adminNotes: approved ? 'Approved for contribution tracking' : 'Rejected',
        createFork: false // Set to true if you want to fork to org
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (approved) {
        alert('Submission approved and added to database!');
      } else {
        alert('Submission rejected.');
      }
      
      refetchData();
    } catch (error) {
      console.error('Error reviewing submission:', error);
      alert('Failed to review submission: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleApproveRepo = async (repoId) => {
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      await axios.put(`/admin/repos/${repoId}`, {
        isApproved: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refetchData();
    } catch (error) {
      console.error('Error approving repo:', error);
      alert('Failed to approve repository');
    }
  };

  const handleUnapproveRepo = async (repoId) => {
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      await axios.put(`/admin/repos/${repoId}`, {
        isApproved: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refetchData();
    } catch (error) {
      console.error('Error unapproving repo:', error);
      alert('Failed to unapprove repository');
    }
  };

  const handleDeleteRepo = async (repoId) => {
    if (!window.confirm('Are you sure you want to delete this repository?')) return;
    
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      await axios.delete(`/admin/repos/${repoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refetchData();
    } catch (error) {
      console.error('Error deleting repo:', error);
      alert('Failed to delete repository');
    }
  };

  const handleToggleApproval = async (repo) => {
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      await axios.put(`/admin/repos/${repo.id}`, {
        isApproved: !repo.isApproved
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refetchData();
    } catch (error) {
      console.error('Error toggling approval:', error);
      alert('Failed to update repository');
    }
  };

  const openEditModal = (repo) => {
    setEditingRepo(repo);
    setFormData({
      name: repo.name,
      description: repo.description || '',
      githubUrl: repo.githubUrl,
      language: repo.language || '',
      isPrivate: repo.isPrivate,
      isEligible: repo.isEligible,
      isApproved: repo.isApproved,
      source: repo.source,
      pointsMultiplier: repo.pointsMultiplier,
      sponsorId: repo.sponsor?.id || null
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      githubUrl: '',
      language: '',
      isPrivate: false,
      isEligible: true,
      isApproved: false,
      source: 'org',
      pointsMultiplier: 1,
      sponsorId: null
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const approvedRepos = dbRepos.filter(r => r.isApproved);
  const pendingSubmissions = submissions.filter(s => s.status === 'pending');

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Manage Repositories</h1>
          <p className="text-sm sm:text-base text-muted-foreground">GitHub organization repos, user submissions, and approved repositories</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 w-full sm:w-auto"
        >
          <Plus className="w-4 h-4" />
          Add Repository
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-6 overflow-x-auto">
        <nav className="flex gap-2 sm:gap-4 min-w-max">
          <button
            onClick={() => {
              setActiveTab(TABS.GITHUB_ORG);
              if (githubRepos.length === 0) {
                fetchGithubRepos();
              }
            }}
            className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 border-b-2 font-medium transition-colors text-sm sm:text-base whitespace-nowrap ${
              activeTab === TABS.GITHUB_ORG
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">GitHub Organization</span>
            <span className="sm:hidden">Org</span>
            <span>({githubRepos.length})</span>
          </button>
          <button
            onClick={() => setActiveTab(TABS.USER_SUBMISSIONS)}
            className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 border-b-2 font-medium transition-colors text-sm sm:text-base whitespace-nowrap ${
              activeTab === TABS.USER_SUBMISSIONS
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">User Submissions</span>
            <span className="sm:hidden">Submissions</span>
            <span>({pendingSubmissions.length})</span>
          </button>
          <button
            onClick={() => setActiveTab(TABS.APPROVED)}
            className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 border-b-2 font-medium transition-colors text-sm sm:text-base whitespace-nowrap ${
              activeTab === TABS.APPROVED
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <CheckCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Approved Repos</span>
            <span className="sm:hidden">Approved</span>
            <span>({approvedRepos.length})</span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === TABS.GITHUB_ORG && (
        <GitHubOrgTab
          repos={githubRepos}
          dbRepos={dbRepos}
          loading={fetchingGithub}
          onRefresh={fetchGithubRepos}
          onAddToDb={async (repo) => {
            // Add GitHub repo to database
            const auth = JSON.parse(localStorage.getItem('auth'))
            const token = auth?.token
            await axios.post('/admin/repos', {
              name: repo.name,
              description: repo.description,
              githubUrl: repo.html_url,
              language: repo.language,
              isPrivate: repo.private,
              isEligible: true,
              isApproved: true,
              source: 'org',
              pointsMultiplier: 1
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            refetchData();
          }}
        />
      )}

      {activeTab === TABS.USER_SUBMISSIONS && (
        <UserSubmissionsTab
          submissions={pendingSubmissions}
          onApprove={handleApproveSubmission}
        />
      )}

      {activeTab === TABS.APPROVED && (
        <ApprovedReposTab
          repos={approvedRepos}
          sponsors={sponsors}
          metrics={metrics}
          onEdit={openEditModal}
          onToggleApproval={handleToggleApproval}
          onDelete={handleDeleteRepo}
        />
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingRepo) && (
        <RepoFormModal
          formData={formData}
          setFormData={setFormData}
          sponsors={sponsors}
          isEditing={!!editingRepo}
          onSubmit={editingRepo ? handleUpdateRepo : handleCreateRepo}
          onClose={() => {
            setShowCreateModal(false);
            setEditingRepo(null);
            resetForm();
          }}
        />
      )}
    </div>
  );
};

// GitHub Organization Tab
const GitHubOrgTab = ({ repos, dbRepos, loading, onRefresh, onAddToDb }) => {
  const isRepoInDb = (repoUrl) => {
    return dbRepos.some(r => r.githubUrl === repoUrl);
  };

  return (
    <div>
      <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <p className="text-sm text-muted-foreground">
          Repositories from {process.env.REACT_APP_GITHUB_ORG || 'your organization'}
        </p>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && repos.length === 0 ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {repos.map(repo => {
            const inDb = isRepoInDb(repo.html_url);
            return (
              <div key={repo.id} className="bg-card rounded-lg border border-border p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                      <h3 className="text-base sm:text-lg font-semibold text-card-foreground">{repo.name}</h3>
                      {repo.private && (
                        <span className="px-2 py-1 bg-secondary text-secondary-foreground rounded-full text-xs font-medium">
                          Private
                        </span>
                      )}
                      {inDb && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                          <CheckCircle className="w-3 h-3" />
                          In DB
                        </span>
                      )}
                    </div>
                    <p className="text-sm sm:text-base text-muted-foreground mb-3">{repo.description || 'No description'}</p>
                    <div className="flex items-center gap-3 sm:gap-4 text-sm text-muted-foreground flex-wrap">
                      {repo.language && (
                        <span className="flex items-center gap-1">
                          <Tag className="w-4 h-4" />
                          {repo.language}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4" />
                        {repo.stargazers_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <GitFork className="w-4 h-4" />
                        {repo.forks_count}
                      </span>
                      <a 
                        href={repo.html_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        View on GitHub
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 self-end sm:self-start">
                    {!inDb && (
                      <button
                        onClick={() => onAddToDb(repo)}
                        className="px-3 sm:px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 text-sm"
                      >
                        <span className="hidden sm:inline">Add to Database</span>
                        <span className="sm:hidden">Add to DB</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {repos.length === 0 && (
            <div className="text-center py-12 bg-card rounded-lg border border-border">
              <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No repositories found in the organization</p>
              <button
                onClick={onRefresh}
                className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
              >
                Fetch Repositories
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// User Submissions Tab
const UserSubmissionsTab = ({ submissions, onApprove }) => {
  return (
    <div className="space-y-4">
      {submissions.map(submission => (
        <div key={submission.id} className="rounded-lg border border-border bg-card p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                <h3 className="text-base sm:text-lg font-semibold text-card-foreground">{submission.repoName}</h3>
                <span className="flex items-center gap-1 px-2 py-1 bg-secondary text-secondary-foreground rounded-full text-xs font-medium">
                  <Clock className="w-3 h-3" />
                  Pending
                </span>
              </div>
              <p className="text-sm sm:text-base text-muted-foreground mb-3">{submission.description || 'No description provided'}</p>
              <div className="flex items-center gap-3 sm:gap-4 text-sm text-muted-foreground mb-3 flex-wrap">
                {submission.language && (
                  <span className="flex items-center gap-1">
                    <Tag className="w-4 h-4" />
                    {submission.language}
                  </span>
                )}
                <span>{submission.isPrivate ? 'Private' : 'Public'}</span>
                <a 
                  href={submission.repoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  View on GitHub
                </a>
              </div>
              {submission.submittedBy && (
                <div className="text-sm text-muted-foreground">
                  Submitted by: <span className="font-medium">{submission.submittedBy.username}</span>
                  {' on '}
                  {new Date(submission.submittedAt).toLocaleDateString()}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0 self-end sm:self-start">
              <button
                onClick={() => onApprove(submission.id, true)}
                className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 text-sm"
              >
                <Check className="w-4 h-4" />
                <span>Approve</span>
              </button>
              <button
                onClick={() => onApprove(submission.id, false)}
                className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:opacity-90 text-sm"
              >
                <X className="w-4 h-4" />
                <span>Reject</span>
              </button>
            </div>
          </div>
        </div>
      ))}

      {submissions.length === 0 && (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No pending submissions</p>
        </div>
      )}
    </div>
  );
};

// Approved Repos Tab
const ApprovedReposTab = ({ repos, sponsors, metrics, onEdit, onToggleApproval, onDelete }) => {
  return (
    <div className="space-y-4">
      {repos.map(repo => (
        <div key={repo.id} className="bg-card rounded-lg border border-border p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                <h3 className="text-base sm:text-lg font-semibold text-card-foreground">{repo.name}</h3>
                <span className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                  <CheckCircle className="w-3 h-3" />
                  Approved
                </span>
                {repo.source === 'org' && (
                  <span className="px-2 py-1 bg-secondary text-secondary-foreground rounded-full text-xs font-medium">
                    Org
                  </span>
                )}
                {repo.sponsor && (
                  <span className="px-2 py-1 bg-accent text-accent-foreground rounded-full text-xs font-medium">
                    {repo.sponsor.name}
                  </span>
                )}
              </div>
              <p className="text-sm sm:text-base text-muted-foreground mb-3">{repo.description || 'No description'}</p>
              <div className="flex items-center gap-3 sm:gap-4 text-sm text-muted-foreground flex-wrap">
                {repo.language && (
                  <span className="flex items-center gap-1">
                    <Tag className="w-4 h-4" />
                    {repo.language}
                  </span>
                )}
                <span>Multiplier: {repo.pointsMultiplier}x</span>
                <a 
                  href={repo.githubUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  View on GitHub
                </a>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 self-end sm:self-start">
              <button
                onClick={() => onToggleApproval(repo)}
                className="px-2 sm:px-3 py-1.5 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 text-sm"
                title="Unapprove"
              >
                <span className="hidden sm:inline">Unapprove</span>
                <span className="sm:hidden">Remove</span>
              </button>
              <button
                onClick={() => onEdit(repo)}
                className="p-2 text-primary hover:bg-primary/10 rounded-lg"
                title="Edit"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(repo.id)}
                className="p-2 text-destructive hover:bg-destructive/10 rounded-lg"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}

      {repos.length === 0 && (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No approved repositories yet</p>
        </div>
      )}
    </div>
  );
};

// Repo Form Modal
const RepoFormModal = ({ formData, setFormData, sponsors, isEditing, onSubmit, onClose }) => (
  <div 
    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="modal-title"
  >
    <div className="bg-card text-card-foreground rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="p-6">
        <h2 id="modal-title" className="text-2xl font-bold mb-4">
          {isEditing ? 'Edit Repository' : 'Add New Repository'}
        </h2>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Repository Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-lg focus:ring-2 focus:ring-ring"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-lg focus:ring-2 focus:ring-ring"
              rows="3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">GitHub URL</label>
            <input
              type="url"
              value={formData.githubUrl}
              onChange={(e) => setFormData({ ...formData, githubUrl: e.target.value })}
              className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-lg focus:ring-2 focus:ring-ring"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Language</label>
              <input
                type="text"
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-lg focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Points Multiplier</label>
              <input
                type="number"
                step="0.1"
                value={formData.pointsMultiplier}
                onChange={(e) => setFormData({ ...formData, pointsMultiplier: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-lg focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Sponsor (Optional)</label>
            <select
              value={formData.sponsorId || ''}
              onChange={(e) => setFormData({ ...formData, sponsorId: e.target.value ? parseInt(e.target.value) : null })}
              className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-lg focus:ring-2 focus:ring-ring"
            >
              <option value="">No Sponsor</option>
              {sponsors.map(sponsor => (
                <option key={sponsor.id} value={sponsor.id}>{sponsor.name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isEligible}
                onChange={(e) => setFormData({ ...formData, isEligible: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm font-medium">Eligible for contributions</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isApproved}
                onChange={(e) => setFormData({ ...formData, isApproved: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm font-medium">Approved</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isPrivate}
                onChange={(e) => setFormData({ ...formData, isPrivate: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm font-medium">Private repository</span>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
            >
              {isEditing ? 'Update Repository' : 'Create Repository'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
);

export default ManageRepos;
